import { print } from 'graphql'
import { RemoveCommunicationPushSubscriptionDocument } from '@/graphql/queries/communicationNotificationsQuery.gql.generated'
import { env } from '@/env'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'

export const DEVICE_USER_KEY = 'amitable-notification-device-user'

export async function unbindCommunicationPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) {
    localStorage.removeItem(DEVICE_USER_KEY)

    return
  }
  await subscription.unsubscribe()
  localStorage.removeItem(DEVICE_USER_KEY)
  const response = await fetch(resolvePublicApiUrl(env.VITE_GRAPHQL_URL, ''), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: print(RemoveCommunicationPushSubscriptionDocument),
      variables: { endpoint: subscription.endpoint },
    }),
  })
  if (!response.ok) throw new Error('Push subscription removal failed')
  const body = (await response.json()) as { errors?: Array<unknown> }
  if (body.errors?.length) throw new Error('Push subscription removal failed')
}

export function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const decoded = atob(value.replace(/-/g, '+').replace(/_/g, '/'))

  return Uint8Array.from(decoded, (character) => character.charCodeAt(0))
}

export function pushSupport() {
  const supported =
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const installed =
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && navigator.standalone === true)

  return { supported, needsInstall: ios && !installed }
}

export async function requestDeviceSubscription(
  vapidKey?: string | null,
): Promise<{ status: string; subscription?: PushSubscription }> {
  const support = pushSupport()
  if (!support.supported || !vapidKey) return { status: 'unavailable' }
  if (support.needsInstall) return { status: 'iosInstall' }
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return { status: 'permissionDenied' }
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return { status: 'unavailable' }
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(vapidKey),
    }))

  return { status: 'deviceEnabled', subscription }
}

export async function currentDeviceStatus(userId?: string): Promise<string> {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator))
    return 'unsupported'
  if (Notification.permission === 'denied') return 'permissionDenied'
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  return subscription && localStorage.getItem(DEVICE_USER_KEY) === userId
    ? 'deviceEnabled'
    : 'deviceDisabled'
}
